import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, Loader2, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  supportsPush, getPermissionStatus, isCurrentlySubscribed,
  subscribeToPush, unsubscribeFromPush, isIOS, isStandalonePWA,
  detectPlatform, type Platform,
} from '@/lib/pushNotifications';

const DISMISS_KEY = 'iftmx:notif-card-dismissed-at';
// El dismiss ('Más tarde') caduca a los 7 días para que la tarjeta
// reaparezca y el cliente tenga otra oportunidad de activar.
// Antes el dismiss era permanente y los clientes que dijeron 'Más tarde'
// nunca volvían a verla.
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function isCurrentlyDismissed(): boolean {
  try {
    // Migración del flag viejo '1' al timestamp.
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) {
      const legacy = localStorage.getItem('iftmx:notif-card-dismissed');
      if (legacy === '1') {
        // Convertir al formato nuevo con timestamp actual para que
        // mantenga el dismiss pero ya caduque eventualmente.
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        localStorage.removeItem('iftmx:notif-card-dismissed');
        return true;
      }
      return false;
    }
    const ts = parseInt(raw, 10);
    if (isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

/** Instrucciones específicas por plataforma para activar manualmente
 *  cuando el navegador ya negó el permiso. El web push API no permite
 *  volver a pedir permiso desde código; el cliente DEBE ir a la
 *  configuración del sistema. */
const MANUAL_INSTRUCTIONS: Record<Platform, { title: string; steps: string[] }> = {
  ios: {
    title: 'En tu iPhone:',
    steps: [
      'Abre la app Configuración de iOS',
      'Toca "Notificaciones"',
      'Busca y selecciona "InsaneForceTeam"',
      'Activa "Permitir notificaciones"',
      'Vuelve aquí y recarga la app',
    ],
  },
  android: {
    title: 'En tu Android:',
    steps: [
      'Mantén presionado el ícono de Insane Force Team',
      'Toca "Información de la app"',
      'Entra a "Notificaciones"',
      'Activa el switch principal',
      'Vuelve a la app',
    ],
  },
  desktop: {
    title: 'En tu computadora:',
    steps: [
      'Click en el candado 🔒 junto a la dirección',
      'Cambia "Notificaciones" a "Permitir"',
      'Recarga la página (F5)',
    ],
  },
};

/**
 * Compact card prompting the user to enable push notifications.
 *
 * Behaviour matrix:
 *   - browser doesn't support push → component returns null (silent)
 *   - already subscribed on this device → renders a subtle "✅ activadas" + disable button
 *   - permission denied → shows guidance to fix in browser settings
 *   - default state → big "Activar notificaciones" button
 *
 * Designed to be drop-in at the top of the client home — coaches can
 * also see it in their dashboard so they get pushes for client events.
 */
export function EnableNotificationsCard() {
  const navigate = useNavigate();
  const [supported] = useState<boolean>(() => supportsPush());
  const [permission, setPermission] = useState(() => getPermissionStatus());
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => isCurrentlyDismissed());
  const [platform] = useState<Platform>(() => detectPlatform());

  useEffect(() => {
    if (!supported) return;
    isCurrentlySubscribed().then(setSubscribed);
  }, [supported]);

  // BUG FIX: handleDismiss must be declared BEFORE any conditional return
  // that references it. Previously it was defined after the iOS branch,
  // which caused ReferenceError on iOS devices and a blank screen on
  // mobile (the iOS branch was the only path that triggered the access).
  const handleDismiss = () => {
    // Guardamos timestamp en lugar de '1' fijo para que el dismiss
    // caduque a los 7 días vía DISMISS_TTL_MS.
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setDismissed(true);
  };

  const handleEnable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setPermission('granted');
      setSubscribed(true);
      toast.success('Notificaciones activadas');
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo activar');
      setPermission(getPermissionStatus());
    } finally {
      setBusy(false);
    }
  };

  // iOS Safari can ONLY do web push when the app is installed via
  // "Add to Home Screen" (iOS 16.4+). Chrome iOS and Firefox iOS
  // don't support push at all (Apple restriction). Show explicit
  // instructions instead of a generic "your browser doesn't support".
  const iosNeedsInstall = isIOS() && !isStandalonePWA();
  if (iosNeedsInstall && !dismissed) {
    return (
      <div className="rounded-xl border-2 border-dashed border-accent/40 bg-accent/5 p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">Para recibir notificaciones en iPhone</div>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            Apple solo permite notificaciones cuando la app está instalada en pantalla de inicio.
            Sigue estos pasos:
          </p>
          <ol className="text-xs text-foreground mt-2 space-y-1 list-decimal list-inside">
            <li>Abre esta página en <strong>Safari</strong> (no Chrome)</li>
            <li>Toca el botón <strong>Compartir</strong> (□↑) abajo</li>
            <li>Elige <strong>"Agregar a pantalla de inicio"</strong></li>
            <li>Abre la app desde el ícono nuevo y vuelve aquí</li>
          </ol>
          <button
            onClick={handleDismiss}
            className="mt-3 text-xs font-bold text-muted-foreground hover:text-foreground"
          >
            Más tarde
          </button>
        </div>
      </div>
    );
  }

  if (!supported || dismissed) return null;
  // Hide once subscribed — no nag for users already opted in.
  if (subscribed === true) return null;

  const isDenied = permission === 'denied';
  const instructions = MANUAL_INSTRUCTIONS[platform];

  return (
    <div className="rounded-xl border-2 border-dashed border-accent/40 bg-accent/5 p-4 flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center shrink-0">
        {isDenied
          ? <BellOff className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          : <Bell className="w-5 h-5 text-accent" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm">
          {isDenied ? 'Notificaciones bloqueadas' : 'Activa las notificaciones'}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
          {isDenied
            ? 'Las bloqueaste en algún momento. Para recibir mensajes de tu coach, hay que activarlas manualmente desde la configuración.'
            : 'Recibe avisos de tu coach, recordatorios de pago y confirmaciones de entrenamiento aunque la app esté cerrada.'}
        </p>

        {/* Pasos detallados por plataforma SOLO cuando el cliente está
            bloqueado (iOS ya no deja re-pedir permiso, así que tiene
            que ir a Configuración manual). En estado default, en vez
            de instrucciones, le ofrecemos el video tutorial — más
            efectivo y directo. */}
        {isDenied && (
          <div className="mt-3 rounded-lg bg-background/60 border border-border p-3">
            <div className="text-[11px] font-black uppercase tracking-wider text-foreground mb-2">
              {instructions.title}
            </div>
            <ol className="text-xs text-foreground space-y-1.5 list-decimal list-inside leading-relaxed">
              {instructions.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-3">
          {!isDenied && (
            <button
              onClick={handleEnable}
              disabled={busy}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-accent text-accent-foreground text-xs font-black uppercase tracking-wider hover:bg-accent/90 disabled:opacity-60 transition-colors min-h-[40px]"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
              {busy ? 'Activando…' : 'Activar notificaciones'}
            </button>
          )}
          {!isDenied && (
            // Video tutorial del flujo de activación (28s). Navega a
            // /soporte con deep-link al tutorial — se abre directo
            // sin que el cliente tenga que buscarlo en el catálogo.
            <button
              onClick={() => navigate('/soporte?tutorial=activar-notificaciones')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-secondary/50 hover:bg-secondary text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors min-h-[40px]"
            >
              <PlayCircle className="w-3.5 h-3.5" />
              Ver tutorial
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-secondary/50 transition-colors min-h-[40px]"
          >
            Más tarde
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Minimal switch shown in account-management surfaces (coach
 * settings later). Lets the user toggle off cleanly.
 */
export function NotificationToggle() {
  const [supported] = useState<boolean>(() => supportsPush());
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    isCurrentlySubscribed().then(setSubscribed);
  }, [supported]);

  if (!supported) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setSubscribed(false);
        toast.success('Notificaciones desactivadas');
      } else {
        await subscribeToPush();
        setSubscribed(true);
        toast.success('Notificaciones activadas');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={toggle}
      disabled={busy}
      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary hover:bg-border text-xs font-bold uppercase tracking-wider transition-colors min-h-[40px]"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : subscribed ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
      {subscribed ? 'Notificaciones activas' : 'Activar notificaciones'}
    </button>
  );
}
